// The error catalogue (spec/08-api-design.md §2.6).
//
// One entry per failure. The key is the slug: kebab-case, and the only thing anyone writes. The
// `type` URI and the snake_case `code` are both derived from it, so the three can never disagree —
// which is the failure this catalogue exists to prevent.
//
// Each entry is also the failure's page at `https://errors.{domain}/{slug}`, the address its `type`
// names: `apps/errors` generates one page per slug from this file, and nothing else. So an entry is
// its status and title, what the failure means and what a client does about it — all four required,
// so a new code cannot ship undocumented:
//
//     'session-already-ended': {
//       status: HTTP_STATUS.conflict,
//       title: 'Session already ended',
//       description: 'The session was ended before this request reached it …',
//       resolution: 'Read the session again and show how it ended …',
//     },
//
// `description` and `resolution` are documentation of the contract, read by whoever builds a client —
// a console, an app, the kiosk and station, or a developer calling the API. They are English, one or
// two plain sentences each, in the glossary's words (spec/01), and they say nothing the API itself
// would not: never whether an account exists (§2.6), never why a player is banned. What a person
// sees on a screen is the client's own copy, in `packages/i18n`, which may say it differently for
// its context.
//
// Before adding one, check there is not already a code that means the same thing. A client that
// has to match on two codes for one situation is a contract that has already started to rot.
import { HTTP_STATUS } from "./status.js";
/**
 * Every failure the API can return.
 *
 * The first block is the transport-level set: one per status in §2.6, used when nothing more
 * specific applies. The rest are the domain failures the specification names, each at the status
 * the document that owns it gives.
 */
export const ERROR_CATALOGUE = {
    // Transport level — one per status in spec/08 §2.6.
    'validation-failed': {
        status: HTTP_STATUS.badRequest,
        title: 'Validation failed',
        description: 'The request could not be understood as written: a field is missing, has the wrong type or is out of range, or the query names a parameter the endpoint does not take. `errors[]` lists each field that failed and why.',
        resolution: 'Correct what `errors[]` names, showing each message beside its field, and send the request again. The same request sent unchanged fails the same way.',
    },
    unauthenticated: {
        status: HTTP_STATUS.unauthorized,
        title: 'Not signed in',
        description: 'The request carried no session, token or API key the API accepts: none was sent, or the one sent has expired or been revoked.',
        resolution: 'Sign in again or get a new token, then repeat the request. Retrying with the same credentials fails the same way.',
    },
    forbidden: {
        status: HTTP_STATUS.forbidden,
        title: 'Not allowed',
        description: 'The caller is signed in, but its role does not give it the permission this action needs at this scope: the Platform, an organisation or a venue.',
        resolution: 'Do not retry, because the answer changes only when the role does. Say the action is not allowed, and hide it where the client reads the caller’s permissions from `GET /v1/me/access`.',
    },
    'not-found': {
        status: HTTP_STATUS.notFound,
        title: 'Not found',
        description: 'Nothing the caller may see is at this address. Where saying that something exists would reveal it, such as a venue in another organisation, the API answers this rather than `forbidden`.',
        resolution: 'Check the id and the path, and do not send the same request again. Do not take this answer as proof that the record does not exist.',
    },
    conflict: {
        status: HTTP_STATUS.conflict,
        title: 'Conflict',
        description: 'The request conflicts with the resource as it is now, usually because another request changed it at the same moment. `detail` says what conflicted.',
        resolution: 'Read the resource again, check that the request still makes sense, and send it again if it does.',
    },
    'stale-write': {
        status: HTTP_STATUS.preconditionFailed,
        title: 'Stale write',
        description: 'The `If-Match` sent with the update is not the resource’s current `ETag`: someone saved a newer version after the client read it. The response carries the current `ETag`.',
        resolution: 'Read the resource again, show the fresh values beside the unsaved ones, and send the update with the new `ETag` once the person has chosen. Never resend it with the old one.',
    },
    unprocessable: {
        status: HTTP_STATUS.unprocessableEntity,
        title: 'Cannot be processed',
        description: 'The request is well formed but cannot be carried out as asked, such as an export with more rows than it may hold. `detail` says why.',
        resolution: 'Change the request as `detail` describes, for example by narrowing its filters, and send it again. The same request sent unchanged fails the same way.',
    },
    'rate-limited': {
        status: HTTP_STATUS.tooManyRequests,
        title: 'Too many requests',
        description: 'The caller sent more requests than its limit allows in the current window. Limits are counted per session, API key, station, account, phone number or IP address, depending on the endpoint.',
        resolution: 'Wait at least as long as the `Retry-After` header says before trying again, and wait longer if it happens again. Tell a person how long to wait rather than letting them retry at once.',
    },
    'internal-error': {
        status: HTTP_STATUS.internalServerError,
        title: 'Something went wrong',
        description: 'The API failed while handling the request, and nothing the request would have changed was kept. It is safe to send again, with the same `Idempotency-Key` where the endpoint takes one.',
        resolution: 'Retry once after a short wait. If it keeps failing, show the `requestId` so the person can quote it to support: it identifies this request in the API’s logs and traces.',
    },
    // The api is at its capacity for database work right now (2026-10-13 load test): a request that would have
    // waited for a connection is answered at once instead. Nothing was changed.
    'service-unavailable': {
        status: HTTP_STATUS.serviceUnavailable,
        title: 'Busy, try again',
        description: 'The API is handling as much as it can at this moment and could not get to this request in time, so nothing it would have changed was kept.',
        resolution: 'Wait at least as long as the `Retry-After` header says, then send the same request again, with the same `Idempotency-Key` where the endpoint takes one.',
    },
    // Sessions (spec/05 §3.2 start preconditions, §12 edge cases).
    'session-already-ended': {
        status: HTTP_STATUS.conflict,
        title: 'Session already ended',
        description: 'The session ended before this request reached it, so it cannot be ended, changed or extended again. Nothing was charged twice.',
        resolution: 'Read the session again and show how it ended, without retrying. To keep playing, the player starts a new session.',
    },
    'station-unavailable': {
        status: HTTP_STATUS.conflict,
        title: 'Station not available',
        description: 'The station cannot start a session now: it is not locked and waiting for a player, it is disabled, or it is in maintenance.',
        resolution: 'Offer another station, or send the player to staff. A console shows the station’s current mode.',
    },
    'station-reserved': {
        status: HTTP_STATUS.conflict,
        title: 'Station reserved',
        description: 'The station is held for a reservation that starts within the hold window, and the player starting the session is not the one who holds it.',
        resolution: 'Offer another station. The player who holds the reservation can start a session here.',
    },
    'wake-unavailable': {
        status: HTTP_STATUS.conflict,
        title: 'Cannot wake the station',
        description: 'The station cannot be woken now: neither the venue’s boot server nor another station at the venue is online to relay the Wake-on-LAN packet, or the station’s network address is not on record.',
        resolution: 'Turn the station on by hand, or try again once the boot server or another station at the venue is online to relay. Pairing a station records its network address.',
    },
    'player-banned': {
        status: HTTP_STATUS.forbidden,
        title: 'Player is banned',
        description: 'A ban in force stops the player doing this here: at the venue, at its organisation or on the Platform. The answer says neither which one nor why.',
        resolution: 'Do not retry. Show one neutral message whatever the ban’s scope, as the kiosk’s “Please see the front desk” does, and never a reason.',
    },
    'id-check-required': {
        status: HTTP_STATUS.forbidden,
        title: 'ID check required',
        description: 'The venue checks a player’s identity before a session starts, and the check its rule asks for has not been done for this player.',
        resolution: 'Start the ID check, in the kiosk where the venue offers one, or send the player to the desk. Start the session again once the check is done.',
    },
    'age-restricted': {
        status: HTTP_STATUS.forbidden,
        title: 'Age restricted',
        description: 'The station’s class has an age limit, and the player is under it by the date of birth the venue relies on, verified or declared.',
        resolution: 'Do not retry on this station. Offer one in a class without the limit.',
    },
    'already-in-session': {
        status: HTTP_STATUS.conflict,
        title: 'Already in a session',
        description: 'The player already has a session that has not ended, at this venue or another. A player has at most one at a time across the Platform.',
        resolution: 'Offer to move the running session to this station, or ask the player to sign out of the other one first. Staff can end the other session from the console.',
    },
    'profile-incomplete': {
        status: HTTP_STATUS.unprocessableEntity,
        title: 'Profile incomplete',
        description: 'The venue needs profile fields the player has not filled in, such as a verified phone number, before a session can start.',
        resolution: 'Ask the player for the missing fields, as the kiosk’s short form does, save them, and start the session again.',
    },
    'no-funding': {
        status: HTTP_STATUS.paymentRequired,
        title: 'Nothing to fund the session',
        description: 'Nothing can pay for the session: no pass, wallet balance or membership time the player holds is valid on this station, and neither grace time nor postpaid applies.',
        resolution: 'Offer what would fund it, such as a pass for this station class, a wallet top-up or a membership, or send the player to staff. Start the session again once one is in place.',
    },
    'pass-required': {
        status: HTTP_STATUS.paymentRequired,
        title: 'A pass is needed to play here',
        description: 'This venue has turned pay as you go off (`sessions.payAsYouGoEnabled`), so play time comes only from passes and time staff add. The player holds no pass that is valid on this station now.',
        resolution: 'Offer a pass for this station class — at the kiosk or the desk — or ask staff to add time. Start the session again once the player holds one.',
    },
    // The wallet (spec/06 §5, F-WAL-01/-02/-04/-06/-07). The organisation's stored value: top-up
    // limits, the enabled flag, the top-up tax point, cash refunds and staff adjustments.
    'wallet-disabled': {
        status: HTTP_STATUS.conflict,
        title: 'The wallet is not enabled here',
        description: 'The organisation has not turned its wallet on (`wallet.enabled`), so it cannot be topped up or spent from.',
        resolution: 'Enable the wallet in the organisation’s settings before topping up or spending, or take payment another way.',
    },
    'wallet-limit': {
        status: HTTP_STATUS.conflict,
        title: 'Top-up outside the wallet’s limits',
        description: 'The top-up is below the minimum, above the maximum per top-up, or would take the balance over the maximum the organisation allows (`wallet.minTopupAmount`, `maxTopupAmount`, `maxBalanceAmount`) — or a sale refunded to the wallet would take it over that maximum. `detail` says which.',
        resolution: 'Show the limit `detail` names and offer an amount within it, or refund to the original tender instead. The same amount is refused every time.',
    },
    'wallet-topup-tax-at-sale': {
        status: HTTP_STATUS.conflict,
        title: 'Top-ups are taxed at sale here',
        description: 'The organisation taxes wallet top-ups at the point of sale (`tax.walletTopupTaxPoint` = `sale`), which needs the tax engine that is not built yet; the wallet foundation can only add value untaxed, taxed where it is spent (`redemption`).',
        resolution: 'Set `tax.walletTopupTaxPoint` to `redemption` to top up now; taxing top-ups at sale is not supported yet.',
    },
    'change-to-wallet-unavailable': {
        status: HTTP_STATUS.conflict,
        title: 'The change cannot go to the wallet',
        description: 'A cash overpayment’s change cannot be credited to the player’s wallet on this payment: the tender is not cash, the sale names no player, nothing was overpaid, or the venue’s wallet is off or taxes top-ups at sale rather than redemption.',
        resolution: 'Hand the change back in cash instead, or attach a player and take the payment again where the wallet is on and taxes top-ups at redemption. The same payment is refused the same way.',
    },
    'wallet-insufficient-cash': {
        status: HTTP_STATUS.conflict,
        title: 'The wallet does not hold that much cash',
        description: 'The refund or debit is larger than the cash the wallet holds. A wallet refund gives back cash the player paid in, and a debit adjustment removes cash, so neither may take the cash balance below zero; a bonus balance is never refunded.',
        resolution: 'Refund or debit no more than the wallet’s current cash balance, or read the balance again if it has moved.',
    },
    // On account (spec/06 §8.6, F-POS-07): the checkout tender that charges a sale to the player's
    // receivable. Off by default (`credit.enabled`), and bounded by the player's effective limit.
    'credit-disabled': {
        status: HTTP_STATUS.conflict,
        title: 'On account is off here',
        description: 'The organisation has not turned on account on (`credit.enabled`), so a sale cannot be charged to a player’s account.',
        resolution: 'Take payment another way, or turn on account on in the organisation’s settings first.',
    },
    'credit-limit-exceeded': {
        status: HTTP_STATUS.conflict,
        title: 'Over the player’s credit limit',
        description: 'Charging this amount would take what the player owes over their effective credit limit (`credit.limitAmount`, or the limit set for the player). `detail` gives the limit.',
        resolution: 'Charge less to the account and take the rest by another tender, collect what the player already owes, or raise their limit (`credit:manage`).',
    },
    'wallet-insufficient-funds': {
        status: HTTP_STATUS.conflict,
        title: 'The wallet does not hold enough',
        description: 'The wallet payment is larger than the player’s wallet holds — its cash and bonus together — or the player has no wallet here. A wallet never goes below zero.',
        resolution: 'Read the wallet balance, take no more than it holds from the wallet, and take the rest by another tender.',
    },
    'wallet-refunds-off': {
        status: HTTP_STATUS.conflict,
        title: 'Cash refunds are turned off here',
        description: 'The organisation does not allow wallet cash to be refunded (`wallet.cashRefundsAllowed` is off), so a top-up cannot be reversed to a tender.',
        resolution: 'Turn `wallet.cashRefundsAllowed` on in the organisation’s settings to refund wallet cash, or let the player spend the balance instead.',
    },
    'wallet-adjustment-over-limit': {
        status: HTTP_STATUS.conflict,
        title: 'Adjustment over the allowed limit',
        description: 'The wallet adjustment is larger than the effective limit for this member — the lower of `wallet.adjustMaxAmount` and the role’s own limit. `detail` gives the limit.',
        resolution: 'Adjust by no more than the limit `detail` names, or ask a member whose role allows a larger adjustment.',
    },
    // Online payments (spec/06 §9.3, §9.5, F-PAY-03). `online-payments-unavailable`: a gate is closed —
    // no payment provider is configured for the deployment, or the venue has not turned online payments
    // on (`payments.onlineEnabled`). `webhook-signature-invalid`: a provider webhook whose signature does
    // not match its raw body, or whose timestamp is more than five minutes old; nothing is recorded.
    'online-payments-unavailable': {
        status: HTTP_STATUS.conflict,
        title: 'Online payments are not available here',
        description: 'Online payments cannot be taken at this venue: no payment provider is configured, the venue has not turned online payments on (`payments.onlineEnabled`), or the organisation’s payee at the provider is not active yet. `detail` says which gate is closed.',
        resolution: 'Take payment with another tender. The same request fails until the closed gate is opened.',
    },
    // The payment provider timed out, could not be reached or answered with a server error (ADR-0061). Nothing was
    // changed at the provider that the platform knows of; the request can be retried.
    'payment-provider-unavailable': {
        status: HTTP_STATUS.serviceUnavailable,
        title: 'Payment provider unavailable',
        description: 'The payment provider could not be reached, timed out or answered with an error of its own. Nothing was recorded.',
        resolution: 'Retry in a little while. If it keeps failing, take payment another way and check the provider’s status.',
    },
    'webhook-signature-invalid': {
        status: HTTP_STATUS.unauthorized,
        title: 'Webhook signature not accepted',
        description: 'The webhook’s signature does not match its raw body under the provider’s signing secret, or its timestamp is more than five minutes old. Nothing was recorded.',
        resolution: 'A provider should sign the exact bytes it sends and deliver promptly; a stale delivery is retried by the provider with a fresh signature. Do not resend the same bytes and headers.',
    },
    // Passes (spec/06 §3, F-PRC-01/-04/-05). A versioned pass product funds play; a holding is sold or
    // granted, and refunded or voided under rules. `pass-product-inactive`: an inactive or archived
    // product cannot be sold or granted (checked at fulfilment, §3.1). `pass-not-refundable`: the
    // holding does not meet the full- or pro-rata-refund rules and the member has no override (§3.4).
    // `pass-not-voidable`: seconds have already been consumed, so a void is refused — refund instead
    // (§3.4, §12).
    'pass-product-inactive': {
        status: HTTP_STATUS.conflict,
        title: 'Pass product is not on sale',
        description: 'The pass product is inactive or archived, so a holding cannot be sold or granted from it. An inactive product is refused at fulfilment, not only in the catalogue.',
        resolution: 'Activate the product, or pick one that is on sale, then sell or grant again.',
    },
    'pass-not-refundable': {
        status: HTTP_STATUS.conflict,
        title: 'Pass cannot be refunded',
        description: 'The holding is outside the full-refund window and pro-rata refunds are off, or it is already refunded, voided or expired, and the member has no refund override.',
        resolution: 'Void the holding if it was a mistake, or ask a member with the refund override, whose action needs a reason. Do not retry the same refund unchanged.',
    },
    // Buying a pass from the kiosk (F-KIO-14, spec/12 §7.1). `pass-not-buyable-here`: the product is not sold
    // on the `kiosk` channel, is outside its sale window or scope, or is not usable on this station's class.
    // `pass-purchase-guest`: a kiosk purchase (and so on account) needs a registered player. `pass-purchase-closed`: the purchase is
    // already paid and applied, so it cannot be cancelled.
    'pass-not-buyable-here': {
        status: HTTP_STATUS.conflict,
        title: 'This pass cannot be bought on this PC',
        description: 'The pass is not sold at the kiosk, is outside its sale window, is not offered at this venue, or does not cover this station’s class, so it cannot be bought from this PC.',
        resolution: 'Buy a pass listed for this PC, or buy this one at the front desk. The same request is refused every time.',
    },
    'pass-purchase-guest': {
        status: HTTP_STATUS.conflict,
        title: 'Kiosk purchases need a registered player',
        description: 'Only a registered player can buy a pass from the kiosk — and only a registered player can add one to their tab (on account). The player on this session has no registered account.',
        resolution: 'Buy the pass at the front desk, or sign in with a registered account.',
    },
    'pass-purchase-closed': {
        status: HTTP_STATUS.conflict,
        title: 'The purchase is already applied',
        description: 'The pass purchase is paid and its pass is issued, so it can no longer be cancelled. A refund is a staff action on the order.',
        resolution: 'Ask staff at the front desk to refund the order if it was a mistake.',
    },
    // A food order from the kiosk (F-KIO-10, spec/12 §7.5): paying it from the wallet or on account needs a
    // registered player on the station's session; at the desk or by UPI it does not.
    'food-order-guest': {
        status: HTTP_STATUS.conflict,
        title: 'Paying from an account needs a registered player',
        description: 'A kiosk food order can be paid from the wallet or added to a tab only by a registered player, and the station has no registered player in session.',
        resolution: 'Pay at the front desk or by UPI, or sign in with a registered account.',
    },
    // Guests (F-SES-06, F-SES-07, spec/05 §3.4, ADR-0059): a walk-in with no account plays on a guest pass sold at the
    // counter, redeemed by its 6-character ticket code at a PC and claimable into an account for 7 days. A wrong code is
    // one generic answer (`guest-ticket-invalid`) whatever the reason it is wrong; a right code that cannot start play
    // here says why, since only its holder knows it.
    'guests-off': {
        status: HTTP_STATUS.conflict,
        title: 'This venue does not serve guests',
        description: 'Guest passes are off at this venue (`sessions.guestsEnabled`), so none can be sold here and no ticket code is taken at its PCs.',
        resolution: 'Sell the pass to a registered player instead, or turn guests on in the venue’s settings.',
    },
    'guest-ticket-invalid': {
        status: HTTP_STATUS.badRequest,
        title: 'That ticket code is not right',
        description: 'No guest pass at this venue answers to the code: it was mistyped, it is from another venue, its sale was never paid or was cancelled, or it was claimed into an account. The answer is the same for each.',
        resolution: 'Check the code on the receipt and type it again. Several wrong codes in a row lock ticket entry on this PC for a while.',
    },
    'guest-ticket-locked': {
        status: HTTP_STATUS.tooManyRequests,
        title: 'Too many wrong ticket codes on this PC',
        description: 'Several wrong ticket codes were typed on this PC in a short time, so it takes no ticket code for a while. `Retry-After` says how long.',
        resolution: 'Wait until `Retry-After` has passed, or ask staff at the front desk to start the session.',
    },
    'guest-ticket-expired': {
        status: HTTP_STATUS.conflict,
        title: 'This guest ticket has expired',
        description: 'The ticket is past the hours it can be used for after it was sold (`sessions.guestTicketValidityHours`), so it no longer starts play.',
        resolution: 'Ask staff at the front desk. A player can still claim the ticket’s time into their account while its claim window is open.',
    },
    'guest-ticket-used-up': {
        status: HTTP_STATUS.conflict,
        title: 'This guest ticket has no time left',
        description: 'The guest pass behind the ticket has no time left: it was played through, refunded, voided or expired.',
        resolution: 'Buy more time at the front desk.',
    },
    'guest-ticket-not-here': {
        status: HTTP_STATUS.conflict,
        title: 'This guest ticket does not work on this PC',
        description: 'The guest pass behind the ticket does not cover this PC now: another class of station, another venue, or outside the hours it can be used.',
        resolution: 'Use the ticket on a PC its pass covers, or ask staff at the front desk which ones those are.',
    },
    'guest-session': {
        status: HTTP_STATUS.conflict,
        title: 'Not for a guest',
        description: 'The session is a guest’s: a guest plays only on the guest pass they paid for at the counter, so time cannot be put on account, drawn from a wallet or bought at the PC for them.',
        resolution: 'Sell the guest another guest pass at the counter, or have them claim their ticket into an account.',
    },
    'guest-claim-unavailable': {
        status: HTTP_STATUS.conflict,
        title: 'This guest ticket cannot be claimed',
        description: 'The ticket was sold more than 7 days ago, is already claimed, is in play on a PC right now, or has nothing left to move. A code nobody knows is `guest-ticket-invalid` instead.',
        resolution: 'Claim the ticket once its session has ended, within 7 days of buying it. Ask staff at the café when in doubt.',
    },
    'pass-not-voidable': {
        status: HTTP_STATUS.conflict,
        title: 'Pass cannot be voided',
        description: 'Some of the pass’s seconds have already been played, or it is already refunded, voided or expired, so it cannot be voided. A void removes only unused seconds.',
        resolution: 'Refund the holding instead (its rules decide what money is returned), rather than voiding it.',
    },
    // Signing in with a one-time code (spec/07 §2.2, §4.1). `otp-invalid` is a wrong code that can
    // still be retried; `otp-expired` is a code that is gone — expired, used up by five wrong attempts,
    // or never asked for — and the only way on is a new one.
    'otp-invalid': {
        status: HTTP_STATUS.badRequest,
        title: 'Incorrect code',
        description: 'The one-time code does not match the one sent. That code still works until it expires or five wrong attempts use it up.',
        resolution: 'Let the person type the code again. Once it is used up the answer becomes `otp_expired`, and they need a new one.',
    },
    'otp-expired': {
        status: HTTP_STATUS.badRequest,
        title: 'Code expired; ask for a new one',
        description: 'The one-time code can no longer be used: it expired, five wrong attempts used it up, or none was asked for.',
        resolution: 'Ask for a new code and use that one. Trying this code again fails the same way.',
    },
    'phone-number-refused': {
        status: HTTP_STATUS.badRequest,
        title: 'This phone number cannot be used',
        description: 'The phone number is well formed, but it is of a kind the Platform does not send codes to, such as a premium-rate number.',
        resolution: 'Ask the person for another number. The same number is refused every time.',
    },
    // No provider can carry the code — none is configured in this environment, or every configured
    // one failed. The code was **not** sent, and saying 503 rather than 200 is the point: a sign-in
    // screen that says "code sent" when nothing was is a player waiting for an SMS that never comes.
    'otp-delivery-unavailable': {
        status: HTTP_STATUS.serviceUnavailable,
        title: 'Codes cannot be sent right now',
        description: 'No provider could deliver the one-time code, because none is set up or every one that is failed. No code was sent.',
        resolution: 'Say that no code was sent, and let the person try again in a few minutes or sign in another way. Never tell them to wait for a message.',
    },
    // Signing in with a password (spec/07 §2.2, issue #43 rule 5): one answer for an unknown account, a
    // wrong password and a malformed username alike, so the response is not an oracle for which accounts
    // exist. `email-not-verified` is reached only once the password has checked out.
    'invalid-credentials': {
        status: HTTP_STATUS.unauthorized,
        title: 'Incorrect email or password',
        description: 'The sign-in did not succeed. The answer, and the time it takes, are the same for an unknown account, a wrong password and a malformed username, so it never says which it was.',
        resolution: 'Show one message for every case, as the title does, and let the person try again. Repeated attempts are limited, with `rate_limited`.',
    },
    'email-not-verified': {
        status: HTTP_STATUS.forbidden,
        title: 'Email address not verified',
        description: 'The password is right, but the account’s email address has not been verified, so it cannot be signed in to with that address yet.',
        resolution: 'Ask the person to verify the address with the code emailed to it, or to sign in another way, such as with a code sent to their phone.',
    },
    // Consent (spec/20 §6, F-CMP-06). `consent-required`: an account is never created without the terms
    // and the privacy policy accepted, and a new number's code is kept so the player can accept and
    // send it again. `consent-version-outdated`: the documents the client showed are not the ones in
    // force — fetch them, show them, ask again.
    'consent-required': {
        status: HTTP_STATUS.badRequest,
        title: 'Accept the terms and the privacy policy',
        description: 'The request needs documents the person has not accepted: the terms and the privacy policy for an account made at sign-in, or the privacy policy for switching marketing on. At sign-in, the code that checked out is kept.',
        resolution: 'Show the documents in force, listed by `GET /v1/meta`, and ask the person to accept them. At sign-in, send the same code again with the acceptance.',
    },
    // Kiosk sign-up (F-KIO-04, 2026-10-01). `sign-up-disabled`: the venue has `kiosk.allowSignUp` off, so a
    // code for a number or address with no account creates nothing (existing players still sign in).
    // `profile-window-closed`: finishing a just-created profile at the kiosk is allowed only to the station
    // that created it, for a short window.
    'sign-up-disabled': {
        status: HTTP_STATUS.forbidden,
        title: 'Accounts are not created at this venue’s PCs',
        description: 'The code checked out, but it names no account, and this venue does not create accounts at its PCs. Players who already have an account sign in as usual.',
        resolution: 'Say that new accounts are made at the front desk or in the app, and offer to go back to signing in.',
    },
    // Phone sign-in off (2026-10-08, the owner's instruction; `auth.phoneSignIn`, spec/07 §3.1). One platform switch,
    // HQ's: while it is off nobody signs in, signs up or verifies a number with a phone code, anywhere, and no code is
    // sent. `phone-invitations-off`: an invitation cannot go to a phone number while it is off, only to an email address.
    'phone-sign-in-off': {
        status: HTTP_STATUS.forbidden,
        title: 'Phone sign-in is turned off',
        description: 'Signing in, creating an account or verifying a number with a phone and a one-time code is turned off on the Platform for now. No code was sent and nothing changed.',
        resolution: 'Say that phone sign-in is not available right now and offer email, a password or Google instead. Hide the phone options: `GET /v1/meta` (`signIn.phone`) says whether they are on, and at a kiosk `GET /v1/station/sign-in/options`.',
    },
    'phone-invitations-off': {
        status: HTTP_STATUS.conflict,
        title: 'Invitations by phone are turned off',
        description: 'Phone sign-in is turned off on the Platform, so an invitation can be sent only to an email address. Nothing was sent and no invitation was made.',
        resolution: 'Ask for the person’s email address and invite them by email.',
    },
    // The station sign-in handoff (ADR-0051, F-KIO-20): a sign-in finished in a browser or on a phone for one PC.
    'sign-in-handoff-unavailable': {
        status: HTTP_STATUS.conflict,
        title: 'Sign-in link used, cancelled or expired',
        description: 'The link that signs a player in at a PC works once, for three minutes, until that PC cancels it. This one is unknown, past its time, cancelled or already used.',
        resolution: 'Do not retry. Ask the player to start again at the PC, which shows a new code.',
    },
    // Linking a provider from the kiosk during a session (ADR-0053, F-KIO-21).
    'account-already-linked': {
        status: HTTP_STATUS.conflict,
        title: 'Already linked',
        description: 'The player’s account already has this provider linked, so there is nothing to link.',
        resolution: 'Read the connected accounts again and show it as linked. To use a different account with that provider, unlink it on the web first.',
    },
    'not-on-site': {
        status: HTTP_STATUS.forbidden,
        title: 'Not at the venue',
        description: 'The venue asks that a PC is signed in to from the venue itself (`auth.qrRequireOnSite`), and this request did not come from the venue’s connection.',
        resolution: 'Say to connect to the venue’s Wi-Fi, or to sign in at the PC itself, and try again.',
    },
    'profile-window-closed': {
        status: HTTP_STATUS.forbidden,
        title: 'The profile can no longer be finished here',
        description: 'Finishing a new account’s profile at a kiosk is allowed only from the station that created it, for a short time after, and only for what the account still lacks. The grant is unknown, expired or used from elsewhere.',
        resolution: 'Carry on without it: the player can finish their profile later in the app or on the web.',
    },
    'consent-version-outdated': {
        status: HTTP_STATUS.conflict,
        title: 'The terms or privacy policy have changed',
        description: 'The versions of the terms or the privacy policy the client showed are not the ones in force now.',
        resolution: 'Read the documents in force from `GET /v1/meta`, show them, and ask for acceptance again.',
    },
    // Account security (spec/07 §3.3, F-IAM-12). `reauthentication-required`: changing an email address
    // or a phone number needs a session signed in within the last ten minutes (`freshAge`) — sign in
    // again, then ask again. `contact-in-use`: the new address or number, once proved yours with its
    // code, belongs to another account.
    'reauthentication-required': {
        status: HTTP_STATUS.forbidden,
        title: 'Sign in again to do this',
        description: 'Changing an email address or a phone number needs a session signed in within the last ten minutes, and this session is older.',
        resolution: 'Ask the person to sign in again, then repeat the change from the new session.',
    },
    'contact-in-use': {
        status: HTTP_STATUS.conflict,
        title: 'That email address or phone number is on another account',
        description: 'The new email address or phone number, proved to be the caller’s with its code, already belongs to another account. The API says so only once the code checks out.',
        resolution: 'Say the address or number is in use, and ask for another one.',
    },
    // Money (spec/06 §1.6, §5.7, spec/08 §2.9).
    'price-changed': {
        status: HTTP_STATUS.conflict,
        title: 'Price changed',
        description: 'The total the server computes now differs from the `expectedTotal` the client sent, because a price, a discount or a tax changed after the cart was quoted. The response carries the fresh quote.',
        resolution: 'Show the new total and ask the person to confirm it, then send the request again with the new `expectedTotal`. Never go ahead at the old price.',
    },
    // ADR-0031: only cash needs a drawer, and it goes into the person's own open drawer (or the one shared drawer).
    'cash-needs-shift': {
        status: HTTP_STATUS.conflict,
        title: 'Cash needs a shift',
        description: 'The venue counts its cash drawers per shift, and the person taking this cash holds no open drawer here (in a shared-drawer venue, none is open), so nobody could say which drawer it went into. Card, UPI and wallet are not affected.',
        resolution: 'Start a shift (count the float) or join the shared one, then take the cash again — or take this payment by card or UPI.',
    },
    'discount-limit-reached': {
        status: HTTP_STATUS.conflict,
        title: 'Discount limit reached',
        description: 'The discount has been redeemed as many times as it may be, and another order took its last use first.',
        resolution: 'Quote the cart again without the discount, show the new total, and let the person decide.',
    },
    // A discount code entered at the kiosk (F-POS-02, spec/06 §8.4, spec/12 §7.5): why the code does not apply.
    // The console's own route answers `not-found` / `conflict`; the station routes name the reason, so the kiosk
    // can say it in words.
    'discount-code-unknown': {
        status: HTTP_STATUS.notFound,
        title: 'No such discount code',
        description: 'No discount code of this organisation matches what was entered.',
        resolution: 'Say the code was not recognised and let the person check it and type it again.',
    },
    'discount-code-inactive': {
        status: HTTP_STATUS.conflict,
        title: 'Discount code not active',
        description: 'The discount code exists but is switched off, or its validity window has not started yet.',
        resolution: 'Say the code cannot be used right now. Retrying the same code does not help.',
    },
    'discount-code-expired': {
        status: HTTP_STATUS.conflict,
        title: 'Discount code expired',
        description: 'The discount code’s validity window has ended.',
        resolution: 'Say the code has expired, and carry on without it.',
    },
    'discount-code-minimum': {
        status: HTTP_STATUS.conflict,
        title: 'Order below the code’s minimum',
        description: 'The discount code needs a larger order than this one.',
        resolution: 'Say how the code applies to a larger order, and let the person add to it or carry on without the code.',
    },
    // Redeeming a coupon at the kiosk (F-POS-06, spec/06 §8.5, spec/12 §8): why the code grants nothing.
    'coupon-unknown': {
        status: HTTP_STATUS.notFound,
        title: 'No such coupon',
        description: 'No coupon matches the code entered. The attempt counts towards the player’s failed-attempt limit.',
        resolution: 'Say the code was not recognised and let the person check it and type it again.',
    },
    'coupon-no-player': {
        status: HTTP_STATUS.conflict,
        title: 'No registered player to redeem for',
        description: 'A coupon is redeemed for a registered player, and nobody with a registered account is signed in on this PC.',
        resolution: 'Ask the player to sign in with their account, or redeem the coupon at the front desk.',
    },
    'coupon-inactive': {
        status: HTTP_STATUS.conflict,
        title: 'Coupon not active',
        description: 'The coupon’s batch is switched off, or its validity window has not started yet.',
        resolution: 'Say the coupon cannot be redeemed right now. Retrying the same code does not help.',
    },
    'coupon-expired': {
        status: HTTP_STATUS.conflict,
        title: 'Coupon expired',
        description: 'The coupon’s validity window has ended.',
        resolution: 'Say the coupon has expired.',
    },
    'coupon-used-up': {
        status: HTTP_STATUS.conflict,
        title: 'Coupon used up',
        description: 'The coupon has been redeemed as many times as it may be.',
        resolution: 'Say the coupon has already been used.',
    },
    'coupon-already-redeemed': {
        status: HTTP_STATUS.conflict,
        title: 'Coupon already redeemed by this player',
        description: 'The player has already redeemed as many coupons of this batch as one player may.',
        resolution: 'Say the player has already redeemed this coupon.',
    },
    'coupon-not-valid-here': {
        status: HTTP_STATUS.conflict,
        title: 'Coupon not valid at this venue',
        description: 'The coupon may be redeemed only at other venues of the organisation.',
        resolution: 'Say the coupon is not valid at this venue.',
    },
    // Access and plan (spec/07 §6.4, spec/14 §4.2, spec/18 §2).
    'second-factor-required': {
        status: HTTP_STATUS.forbidden,
        title: 'Second factor required',
        description: 'The caller’s role needs a second factor, and this session was signed in without one.',
        resolution: 'Ask for the second factor, or for enrolling one if the person has none yet, then repeat the request from the session that has it.',
    },
    // Members and invitations (spec/07 §6.5). An invitation that was used, revoked or has expired is
    // gone for good; the inviter sends a new one. `invitation-contact-mismatch` is signing in with a
    // number or an address other than the one invited. `last-owner` is the invariant of spec/04 §2:
    // an organisation always has an owner.
    'invitation-unavailable': {
        status: HTTP_STATUS.conflict,
        title: 'Invitation used, revoked or expired',
        description: 'The invitation cannot be accepted: it was used already, it was withdrawn, or it has expired. It does not come back.',
        resolution: 'Ask whoever sent it for a new invitation.',
    },
    'invitation-contact-mismatch': {
        status: HTTP_STATUS.forbidden,
        title: 'Invitation is for someone else',
        description: 'The invitation was sent to an email address or phone number other than the one the caller is signed in with.',
        resolution: 'Sign in with the email address or phone number the invitation was sent to, then accept it again.',
    },
    'already-a-member': {
        status: HTTP_STATUS.conflict,
        title: 'Already a member',
        description: 'The person is already a staff member of this organisation.',
        resolution: 'Change the member’s role or venues instead of inviting them again.',
    },
    'last-owner': {
        status: HTTP_STATUS.conflict,
        title: 'An organisation needs an owner',
        description: 'The change would leave the organisation with no active owner, and an organisation always has one.',
        resolution: 'Make another member an owner first, then make the change.',
    },
    // Custom roles (spec/07 §6.2, F-IAM-11). `role-name-taken` is a name a built-in role or another
    // custom role of the organisation already has; `custom-role-limit-reached` is the twenty-first;
    // `role-in-use` is deleting a role a member holds — suspended members included, because they get
    // it back when reinstated — or a pending invitation names.
    'role-name-taken': {
        status: HTTP_STATUS.conflict,
        title: 'That role name is taken',
        description: 'A built-in role or another custom role of the organisation already has this name.',
        resolution: 'Choose another name.',
    },
    'custom-role-limit-reached': {
        status: HTTP_STATUS.conflict,
        title: 'This organisation has as many custom roles as it may',
        description: 'The organisation already has twenty custom roles, the most it may have.',
        resolution: 'Delete a custom role it no longer uses, or change one, before adding another.',
    },
    'role-in-use': {
        status: HTTP_STATUS.conflict,
        title: 'Role still in use',
        description: 'The role cannot be deleted while a member holds it, suspended members included, or while a pending invitation names it.',
        resolution: 'Give those members another role and withdraw or change those invitations, then delete the role.',
    },
    // The staff PIN at shared counter devices (spec/07 §6.7, F-IAM-10). `device-locked` is a request
    // from a counter device nobody has unlocked — show the PIN pad; `staff-pin-incorrect` a wrong PIN,
    // or a member with no PIN at this venue; `staff-pin-locked` the fifth failure's lock, which only a
    // manager's reset lifts; `shared-device` a request a counter device may not make at all, because
    // it would act on the account of the manager who opened it.
    'device-locked': {
        status: HTTP_STATUS.forbidden,
        title: 'This counter device is locked',
        description: 'The request came from a shared counter device that no staff member has unlocked with their PIN.',
        resolution: 'Show the PIN pad, and repeat the request once a staff member has unlocked the device.',
    },
    'staff-pin-incorrect': {
        status: HTTP_STATUS.badRequest,
        title: 'Incorrect PIN',
        description: 'The PIN is not right for this venue: it is wrong, or the staff member has no PIN here. The answer does not say which.',
        resolution: 'Let the person try again. After the fifth wrong try the PIN locks, and the answer becomes `staff_pin_locked`.',
    },
    'staff-pin-locked': {
        status: HTTP_STATUS.forbidden,
        title: 'PIN locked; a manager must reset it',
        description: 'The staff member’s PIN at this venue is locked after five wrong tries.',
        resolution: 'Ask a manager to reset the PIN. Trying again does not unlock it.',
    },
    'shared-device': {
        status: HTTP_STATUS.forbidden,
        title: 'Not on a shared counter device',
        description: 'A shared counter device may not make this request, because it would act on the account of the member who opened the device, such as setting a PIN or accepting an invitation.',
        resolution: 'Make the request from the person’s own device, signed in as them.',
    },
    // The step-away lock PIN (spec/05 §6, spec/07 §4.4; F-SES-12). `session-pin-incorrect` is a wrong
    // PIN; `session-unlock-locked` is the fifth wrong try's minute-long lock, which lifts on its own or
    // when staff unlock the station from the console (spec/05 §6 rule 5, F-SES-15 rule 4).
    'session-pin-incorrect': {
        status: HTTP_STATUS.badRequest,
        title: 'Incorrect PIN',
        description: 'The PIN entered to unlock this session is not right. `detail` says how many tries remain before unlocking is locked.',
        resolution: 'Let the player try again. After the fifth wrong try unlocking locks, and the answer becomes `session_unlock_locked`.',
    },
    'session-unlock-locked': {
        status: HTTP_STATUS.tooManyRequests,
        title: 'Unlocking is locked; wait or ask staff',
        description: 'Five wrong PINs in a row locked unlocking for a short while. `retryAfterSeconds` says for how long.',
        resolution: 'Wait out the penalty and try again, unlock with a password or phone approval, or ask staff to unlock the station from the console — which clears it at once.',
    },
    'entitlement-required': {
        status: HTTP_STATUS.paymentRequired,
        title: 'Not included in the plan',
        description: 'The organisation’s subscription does not include what this request needs, such as a module, more stations or a setting that depends on one.',
        resolution: 'Do not retry. Say that the organisation’s plan does not include it.',
    },
    // Settings (spec/03 §9, F-ORG-02). HQ locked the setting at platform or organisation scope, so no
    // scope below the lock may change it — the detail names which scope holds the lock.
    'setting-locked': {
        status: HTTP_STATUS.forbidden,
        title: 'Setting locked',
        description: 'HQ has locked this setting for the Platform or for the organisation, so it cannot be changed at this scope. `detail` names the scope that holds the lock.',
        resolution: 'Show the setting as locked and read only. Only a change at the scope that holds the lock can change it.',
    },
    // The sales pipeline (F-HQ-39, spec/14 §16).
    'do-not-contact': {
        status: HTTP_STATUS.conflict,
        title: 'Marked do not contact',
        description: 'The lead asked not to be contacted, so no outreach to it is recorded or opened. Nothing was written.',
        resolution: 'Do not message this lead. Clear the do-not-contact mark only if they have asked to hear from us again.',
    },
    'daily-outreach-cap': {
        status: HTTP_STATUS.conflict,
        title: 'Daily limit on first messages reached',
        description: 'This member has already opened as many first messages today as `leads.dailyOutreachCap` allows. Follow-ups to leads already contacted do not count.',
        resolution: 'Wait until tomorrow in the deployment’s time zone, or work through follow-ups instead.',
    },
    'lead-converted': {
        status: HTTP_STATUS.conflict,
        title: 'Lead already converted',
        description: 'The lead already has an organisation, so it cannot be converted a second time.',
        resolution: 'Open the lead’s organisation instead.',
    },
    'lead-phone-taken': {
        status: HTTP_STATUS.conflict,
        title: 'That phone number is already a lead',
        description: 'Another lead already has this phone number. A number belongs to one lead.',
        resolution: 'Open the existing lead instead of making a second one.',
    },
    'lead-no-phone': {
        status: HTTP_STATUS.conflict,
        title: 'The lead has no phone number',
        description: 'A WhatsApp message needs a phone number and this lead has none.',
        resolution: 'Add a phone number to the lead, or use another channel.',
    },
    'lead-no-instagram': {
        status: HTTP_STATUS.conflict,
        title: 'The lead has no Instagram handle',
        description: 'An Instagram message needs a handle and this lead has none.',
        resolution: 'Add the Instagram handle to the lead, or use another channel.',
    },
    // Idempotent creates (spec/08 §2.2, F-CMP-02). The `Idempotency-Key` was already used for a
    // create with a different body: a retry must send the request it is retrying, and a new create
    // needs a new key.
    'idempotency-key-reused': {
        status: HTTP_STATUS.unprocessableEntity,
        title: 'Idempotency key used for a different request',
        description: 'The `Idempotency-Key` was already used for a request with a different body. A key belongs to one request.',
        resolution: 'To retry, send exactly the same body with the same key. For a new request, send a new key, a fresh UUID.',
    },
    // A card number typed into a free-text payment reference (spec/20 §5, PCI DSS: a counter never stores a
    // primary account number). A 13–19 digit run that passes the Luhn check — spaces and dashes allowed — is refused.
    'card-number-refused': {
        status: HTTP_STATUS.unprocessableEntity,
        title: 'Enter only the last 4 digits',
        description: 'The payment reference looks like a whole card number. Clavian never stores a card number: a reference holds at most the last 4 digits of the card.',
        resolution: 'Enter only the last 4 digits of the card (or the approval code on the card slip) and send the payment again.',
    },
    // Organisations and venues (spec/04 §3, F-ORG-01).
    'slug-taken': {
        status: HTTP_STATUS.conflict,
        title: 'That slug is taken',
        description: 'Another organisation or venue already uses this slug, and a slug names one only.',
        resolution: 'Choose another slug and send the request again.',
    },
    // ADR-0046: a café is invited with its business name alone, and its owner completes the legal name and the
    // registered address during setup. A GST document is issued under both, so a venue records a `regular` or
    // `composition` tax registration only once they are recorded (spec/06 §10.3).
    'business-details-required': {
        status: HTTP_STATUS.conflict,
        title: 'Business details needed first',
        description: 'The organisation’s legal name or registered address is not recorded yet, and a tax invoice or a bill of supply is issued under both.',
        resolution: 'Ask an owner to complete the business details in the organisation’s settings, then record the tax registration again. Until then the venue sells with plain bills.',
    },
    // Guided venue setup (ADR-0047, F-HQ-03): going live waits on the venue's essentials.
    'venue-not-ready': {
        status: HTTP_STATUS.conflict,
        title: 'Venue not ready to go live',
        description: 'A venue in `setup` goes live only once its essential setup steps are done — opening hours set, a price players can pay, and a PC paired — and `missingSteps` lists the ones still missing. A venue already live or paused is not asked.',
        resolution: 'Show the steps `missingSteps` names, each with where it is done, and offer Go live again once they are. Read `GET …/venues/{v}/setup` for the whole list.',
    },
    'currency-mismatch': {
        status: HTTP_STATUS.unprocessableEntity,
        title: "Currency differs from the organisation's",
        description: 'The currency given is not the organisation’s, and everything an organisation and its venues sell is in its one currency.',
        resolution: 'Use the organisation’s currency, which `detail` names.',
    },
    // The game catalogue (spec/14 §6, F-CAT-01, F-CAT-07). `catalogue-provider-unavailable` is the
    // metadata provider (IGDB) not being reachable — its Twitch credentials are not configured, or it
    // errored or rate-limited us; the catalogue is HQ-only and gated, so this never reaches a venue.
    // `not-a-base-game` is a picked `igdbId` that does not resolve to a base game we can mirror: it has
    // no base-game root, or the base game has no cover.
    'catalogue-provider-unavailable': {
        status: HTTP_STATUS.serviceUnavailable,
        title: 'Game metadata provider unavailable',
        description: 'The game metadata provider (IGDB) cannot answer right now: it is not configured, or it errored or rate-limited the request.',
        resolution: 'Do not retry immediately. If it stays unavailable, check that the provider’s credentials are configured.',
    },
    // Game media (spec/14 §6, ADR-0028): an art source (SteamGridDB, or IGDB for artworks) that is not
    // configured (`STEAMGRIDDB_API_KEY`, the Twitch credentials) or errored, rate-limited or timed out.
    // HQ-only and gated, like the provider above; never reaches a venue.
    'art-source-unavailable': {
        status: HTTP_STATUS.serviceUnavailable,
        title: 'Art source unavailable',
        description: 'The game-art source (SteamGridDB or IGDB) cannot answer right now: it is not configured, or it errored, timed out or rate-limited the request.',
        resolution: 'Try the other source, or retry later. If it stays unavailable, check that the source’s key is configured.',
    },
    'not-a-base-game': {
        status: HTTP_STATUS.unprocessableEntity,
        title: 'Not a base game',
        description: 'The chosen title does not resolve to a base game the catalogue can mirror: it has no base-game root, or the base game has no cover art.',
        resolution: 'Pick a base game from the search results rather than an edition, a DLC or a bundle.',
    },
    // The kiosk Account and top-ups (F-KIO-15, F-KIO-10; spec/12 §7.1, §8) and split tender (F-KIO-14, spec/06 §8.3).
    'pass-not-queued': {
        status: HTTP_STATUS.conflict,
        title: 'That pass is not waiting on this session',
        description: 'Only a pass queued on the running session, and not yet started, can be pinned to be used next. This one is already in use, played through, or cannot fund play on this station now.',
        resolution: 'Read the account again and offer the pin only on a pass it lists as queued.',
    },
    // A pass scoped to one venue (spec/06 §3.1, §3.3; owner 2026-09-30, #709) used at another venue of the organisation.
    'pass-not-valid-here': {
        status: HTTP_STATUS.conflict,
        title: 'Pass not valid at this venue',
        description: 'The pass was sold for use only at another venue of the organisation (its product is scoped to a single venue), so it cannot fund play here.',
        resolution: 'Say the pass can be used only at the venue it is valid at. Read the account again: it lists only the passes usable here.',
    },
    'wallet-topup-below-minimum': {
        status: HTTP_STATUS.conflict,
        title: 'Top-up below the minimum',
        description: 'The amount is below the organisation’s smallest top-up (`wallet.minTopupAmount`).',
        resolution: 'Offer an amount at or above the minimum the top-up options show. The same amount is refused every time.',
    },
    'wallet-topup-above-maximum': {
        status: HTTP_STATUS.conflict,
        title: 'Top-up above the maximum',
        description: 'The amount is above the organisation’s largest single top-up (`wallet.maxTopupAmount`).',
        resolution: 'Offer an amount at or below the maximum the top-up options show, or top up at the front desk in parts.',
    },
    'wallet-balance-over-maximum': {
        status: HTTP_STATUS.conflict,
        title: 'The balance would pass the maximum',
        description: 'The top-up, with any bonus it earns, would take the wallet over the most it may hold at this organisation (`wallet.maxBalanceAmount`).',
        resolution: 'Offer a smaller amount, or none until the player has spent some of their balance.',
    },
    'wallet-topup-closed': {
        status: HTTP_STATUS.conflict,
        title: 'The top-up is already settled',
        description: 'The top-up request was already paid and credited, so it can no longer be cancelled or paid again.',
        resolution: 'Read the request again and show how it ended. A refund of wallet cash is a staff action.',
    },
    'demo-limit-reached': {
        status: HTTP_STATUS.tooManyRequests,
        title: 'A demo was started too recently',
        description: 'Each person may start one demo window in a cool-down period, and the last one started inside it. The window is the only demo data Clavian keeps.',
        resolution: 'Say the demo has been used and when another can start: the `Retry-After` header, or the window’s `nextStartAt` from `GET /v1/me/demo`.',
    },
    'tender-exceeds-outstanding': {
        status: HTTP_STATUS.conflict,
        title: 'More than the order still owes',
        description: 'A tender on a split payment may be at most what the order still owes; this one is more. The outstanding amount is the authority (`GET /v1/station/sales/{saleId}/outstanding`).',
        resolution: 'Read the outstanding amount and tender no more than it. Pay the whole order from one tender when it covers it.',
    },
    // Moving a café to Clavian (F-HQ-40, ADR-0048, 2026-10-08).
    'migration-file-unreadable': {
        status: HTTP_STATUS.unprocessableEntity,
        title: 'The export could not be read',
        description: 'The uploaded file is larger than the limit, is neither CSV nor XLSX, is damaged, or has more rows than one migration takes; `detail` says which, and nothing in it was kept.',
        resolution: 'Export the players again as CSV (or XLSX, first sheet), check it opens in a spreadsheet, and upload that.',
    },
    'migration-ticks-missing': {
        status: HTTP_STATUS.conflict,
        title: 'Large balances are not ticked',
        description: 'Every balance above the wallet’s maximum (`wallet.maxBalanceAmount`) needs HQ’s own tick before the import runs. `detail` names the rows still unticked.',
        resolution: 'Check each listed balance against the old system’s report, tick it on the review step, and import again.',
    },
    'migration-gst-unanswered': {
        status: HTTP_STATUS.conflict,
        title: 'Say whether GST was charged at top-up',
        description: 'The import carries money over, and whether the old system charged GST when players topped up decides whether that money is taxed again when it is spent. It has not been answered.',
        resolution: 'Answer the GST question on the mapping step (the café’s accountant confirms it), then import.',
    },
    // Claiming a moved profile (F-IAM-13, ADR-0048 §9, §13, 2026-10-08).
    'carried-over-approval-required': {
        status: HTTP_STATUS.forbidden,
        title: 'A manager approves this claim',
        description: 'The money this claim moves is above `migration.counterClaimApprovalAmount` (₹2,000 by default; cash only, never time), so a manager’s staff PIN must approve it: a member holding `wallet:adjust`, other than the person claiming. Nothing moved.',
        resolution: 'Ask a manager to enter their staff PIN in the claim dialog, then claim again.',
    },
    'carried-over-counter-only': {
        status: HTTP_STATUS.conflict,
        title: 'Claim this at the front desk',
        description: 'This moved profile can only be claimed at the counter, where staff see the person: it is an under-18’s (with a parent or guardian present), or the old system banned it. Nothing moved.',
        resolution: 'Go to the front desk of the café with photo ID (and, for an under-18, a parent or guardian).',
    },
    'carried-over-email-mismatch': {
        status: HTTP_STATUS.conflict,
        title: 'Sign in with the email the link went to',
        description: 'A moved balance goes only to the account whose verified email is exactly the one the café’s old system had; the account signed in has another, or has not verified it, so nothing moved.',
        resolution: 'Sign in (or create an account) with the email the message was sent to, verify it, and open the link again.',
    },
    'carried-over-link-invalid': {
        status: HTTP_STATUS.badRequest,
        title: 'This link does not work',
        description: 'The link in the email is not one we sent, was changed, or has run out (it lasts until the claim window ends).',
        resolution: 'Use the link in the newest email from the café, or claim at the café: at a PC ("Moving from our old system?") or at the front desk.',
    },
    // Stock (spec/13 §6.4, ADR-0040; F-FNB-07 … F-FNB-13, 2026-10-07).
    'inventory-disabled': {
        status: HTTP_STATUS.conflict,
        title: 'Stock tracking is off here',
        description: 'The venue has not turned stock tracking on (`inventory.enabled`), so nothing about its stock is recorded: no item, delivery, count, waste, recipe or copy is written while it is off.',
        resolution: 'Turn on "Track stock" in the venue’s Food and drinks settings (it needs `settings:fnb`), then send the request again. A client reads `GET …/inventory/status` and hides the stock actions while it is off.',
    },
    // Clavian Boot (ADR-0063, spec/25 §13.3, §14). The organisation's `boot` add-on licenses a number of PCs (`maxBootPcs`).
    'boot-licence-full': {
        status: HTTP_STATUS.conflict,
        title: 'Every licensed boot PC is in use',
        description: 'The organisation’s Clavian Boot add-on licenses a number of PCs, and that many are already added at its venues, so no other PC can be added. Nothing was changed.',
        resolution: 'Remove a PC that is no longer used, or ask for more licensed PCs on the add-on, then add this one again. Do not retry unchanged.',
    },
    'boot-edit-in-progress': {
        status: HTTP_STATUS.conflict,
        title: 'This image is already being edited',
        description: 'An image is edited on one PC at a time (an edit boot), and an edit of this image is open: it has not been published or discarded yet.',
        resolution: 'Publish or discard the open edit first, then start a new one. A client reads the image’s edit and shows it.',
    },
    'stock-count-incomplete': {
        status: HTTP_STATUS.conflict,
        title: 'Some items have not been counted',
        description: 'A full count sets every item to what is on the shelf, so it is submitted only once every item on it has a counted quantity; `detail` names the items still uncounted.',
        resolution: 'Count the items named, or enter 0 for any that are not there, and submit again. To count only some items, start a spot count instead.',
    },
};
export const ERROR_SLUGS = Object.keys(ERROR_CATALOGUE);
/** `session-already-ended` → `session_already_ended`. */
export function errorCode(slug) {
    return slug.replaceAll('-', '_');
}
export function errorStatus(slug) {
    return ERROR_CATALOGUE[slug].status;
}
export function errorTitle(slug) {
    return ERROR_CATALOGUE[slug].title;
}
export function isErrorSlug(value) {
    return typeof value === 'string' && Object.hasOwn(ERROR_CATALOGUE, value);
}
/** Every `code` in the catalogue, in the order the slugs are declared. */
export const ERROR_CODES = ERROR_SLUGS.map(errorCode);
export const ERROR_REGISTRY = ERROR_SLUGS.map((slug) => ({
    slug,
    code: errorCode(slug),
    status: ERROR_CATALOGUE[slug].status,
}));
/** Whether a string is one of the catalogue's snake_case codes. */
export function isErrorCode(value) {
    return typeof value === 'string' && ERROR_CODE_SET.has(value);
}
const ERROR_CODE_SET = new Set(ERROR_CODES);
/** The status a catalogue code is answered with, or `undefined` for a code the catalogue does not hold. */
export function statusOfCode(code) {
    return ERROR_REGISTRY.find((entry) => entry.code === code)?.status;
}
/**
 * A code as the station relays it. The station answers a kiosk with the problem's code in kebab-case
 * (`wallet-disabled`, as a slug), the API with snake_case (`wallet_disabled`); this reads either and answers the
 * snake_case code, or `undefined` for a string that is neither.
 */
export function normalizeErrorCode(value) {
    if (value === undefined)
        return undefined;
    const code = value.trim().replaceAll('-', '_');
    return isErrorCode(code) ? code : undefined;
}
//# sourceMappingURL=catalogue.js.map